import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { Annotation, AnnotationField, StacTagsError, StacTagsService } from 'src/app/services/stac-tags.service';
import { SceneMetadataRow, SceneMetadataUiService } from 'src/app/services/scene-metadata-ui.service';
import { canUseStac } from 'src/app/services/stac-session.service';
import { AuthService } from 'src/app/services/auth.service';
import { StacConversionService } from 'src/app/services/stac-conversion.service';

/** One field's value for the summary line: its chips, or its text. */
interface SummaryEntry {
  field: AnnotationField;
  tags?: string[];
  text?: string;
}

/**
 * The per-scene metadata row on My Collections — tags, ref ID, owner, classification and any
 * other configured field (plans/stac-custom-metadata.md §7.1, D6, D13, R8, R19; built on
 * plans/stac-custom-tags.md §5 "Display").
 *
 * Self-sufficient on purpose: it is given the three things a scene row already has — the task id,
 * the sensor uuid and the scene — derives the catalog item id, asks StacTagsService for the task's
 * map (one shared request per task) and renders NOTHING when the catalog could not be asked. That
 * is what keeps the 25 000-line collections component untouched apart from one template row.
 *
 * A SUMMARY THAT OPENS IN PLACE (D6). Collapsed, the row is one read-only line: the tags as chips,
 * then each other value, from the SAVED SNAPSHOT — what the server last confirmed. The disclosure
 * control, right after the row's label (never at the line's far end, which a scene table wider
 * than its panel hides), opens a form in the same row, beneath that line, built from the field
 * definitions (D13): a chip list for a tagset, a text box for text, a dropdown for an enum in rank
 * order. The summary line never reflows when the row opens, so the control stays under the
 * pointer — and when opening this row closes one ABOVE it, the scroll is corrected by the height
 * that row lost, for the same reason.
 *
 * DRAFTS (R8). While open the form edits a DRAFT, never the snapshot. Adding or removing a chip,
 * typing, picking a level: the draft only, no request. One Save sends ONLY the fields that differ
 * from the snapshot (R19) — and is disabled while nothing differs, since the endpoint answers an
 * empty save with a 400. Its `200` — the whole annotation as stored — becomes the snapshot and the
 * row closes. A refused value keeps the draft and the form open, with the backend's fixed message;
 * a 404 makes the row unavailable; anything else keeps the draft for a retry. While a save is
 * pending nothing can close the row or switch to another. Cancel drops the draft without a request;
 * closing a row with unsaved changes asks first. Only one row is open at a time
 * (SceneMetadataUiService), and a row with unsaved changes asks before another one opens — the
 * question is scrolled into view and focused, since it may be in another table. Navigating away
 * discards an open draft — accepted in v1, with no route guard.
 *
 * A FIELD THIS ROW HAS NOT READ IS NOT EDITABLE. A field the definitions gained after the task was
 * loaded (a migration, or a field shown again) is not in the row's snapshot, so the row does not
 * know its stored value; editing it would overwrite that value blind. It shows "reload to edit"
 * until the row's own data includes it — after a reload, or after this row's next save, whose
 * `200` carries every active field.
 *
 * Gated on the SAME entitlement as the Archive nav entry — Premium and Enterprise, behind the
 * deployment's STAC dark-launch flag — via the shared `isStacEligible`. Not a second rule that
 * happens to agree: the fields exist to be filtered on in Archive, so a plan that cannot open
 * Archive has no use for them, and the two gates drifting apart is the only way this feature can
 * be wrong in an invisible way. It is a UI gate only; see `isStacEligible` for why. And, as the
 * Data Catalog menu, Receive Data for a permission-based organisation's sub user (`canUseStac`,
 * plans/stac-permission-visibility.md): without it the catalog shows them nothing.
 *
 * AND ON THE SENSOR: Add to STAC is switched on per operator (decision 31), and the task's STAC
 * state — the one shared read the "Add to STAC" button and the scene chips already listen to —
 * says which of the task's sensors it covers. A scene of a sensor that is not `on` has no row at
 * all: "Not in the Data Catalog" under every scene of an operator STAC never touches is not an
 * explanation, it is a puzzle. No request of its own: it listens to StacConversionService.
 *
 * IT IS THE ROW, not a cell in one. The scene row is a dense line of columns roughly 160px wide
 * apiece, so the editor is a full-width row of its own directly beneath the scene. Hence the
 * `tr[app-scene-tags]` selector: the component renders the `<tr>` and its single spanning cell, so
 * it can also make the row VANISH — a `<tr>` left behind by a scene that could not be checked would
 * be a blank line under most scenes. The name stays `scene-tags` (§11 item 5).
 */
@Component({
  // An attribute on <tr>, so the component owns the row it lives in — see the note above.
  selector: 'tr[app-scene-tags]',
  templateUrl: './scene-tags.component.html',
  styleUrls: ['./scene-tags.component.scss'],
})
export class SceneTagsComponent implements OnInit, OnDestroy, SceneMetadataRow {
  @Input() taskId!: string;
  @Input() sensorId!: string;
  @Input() scene: any;

  /** The configured fields, in display order (GET /annotation-fields). */
  fields: AnnotationField[] = [];
  /**
   * What the server last confirmed — the summary always shows this. null = no row at all: not
   * loaded yet, an ineligible plan, or the catalog could not be reached.
   */
  saved: Annotation | null = null;
  /** The form's own copy while the row is open; never the same object as `saved`. */
  draft: Annotation | null = null;
  /**
   * The catalog ANSWERED, and this scene is not in it. Shown as a disabled row with an explanation
   * rather than no row, because an eligible user looking at two scenes — one editable, one with no
   * row at all — reads the second as a bug. Distinct from a failed request, which stays hidden:
   * "not in the archive" would be a false statement about a scene we simply could not check.
   */
  unavailable = false;
  expanded = false;
  saving = false;
  /** Why the row is asking "Discard changes?" — its own close, or another row opening — or null. */
  confirming: 'collapse' | 'switch' | null = null;
  /** The save's message: the backend's fixed text for a refused value, or a retry prompt. */
  formError = '';
  /** Per-field hints, by member — from the definitions, before a round trip. */
  hints: Record<string, string> = {};
  readonly separatorKeys = [ENTER, COMMA] as const;

  /** What the fields are FOR and how to fill them in (the user's wording, 2026-10-29). */
  readonly INFO_TOOLTIP =
    'You may add your own metadata to organise the data in the Data Catalog. Use the dropdown to ' +
    'select from the available options and enter your values.';

  /** Why a disabled row is disabled. Only catalog items can carry them. */
  readonly UNAVAILABLE_TOOLTIP =
    "This scene isn't in the Data Catalog, so nothing can be added to it. Please contact support.";

  /**
   * Collapses the whole row, cell and all. `*ngIf` inside the template can only empty the cell;
   * the `<tr>` is the host and outlives it, and an empty row still draws a line's worth of padding.
   */
  @HostBinding('class.is-empty')
  get isEmpty(): boolean {
    return this.saved === null || !this.covered;
  }

  /**
   * Add to STAC is switched on for this scene's sensor, by the task's STAC state. False until that
   * state is known, so nothing shows before the server has said which sensors a request covers.
   */
  covered = false;

  private itemId: string | null = null;
  private userSub?: Subscription;
  private viewSub?: Subscription;
  private started = false;
  private destroyed = false;
  /** Settles a "Discard changes?" another row is waiting on. */
  private decision: ((closed: boolean) => void) | null = null;
  /** The summary, rebuilt only when the snapshot or the definitions change (see `summary`). */
  private summaryOf: { saved: Annotation | null; fields: AnnotationField[] } | null = null;
  private summaryCache: SummaryEntry[] = [];

  constructor(
    private stacTags: StacTagsService,
    private stac: StacConversionService,
    private ui: SceneMetadataUiService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    /**
     * `authService.user` and NOT `authService.currentUser`: nothing ever assigns the latter but
     * `logout()`, so it reads `undefined` for a signed-in user and a plan check against it refuses
     * EVERYONE. `user` is a BehaviorSubject seeded from localStorage, so it answers at once on a
     * returning session and again when a first login fills it in.
     */
    this.userSub = this.authService.user.subscribe((user) => this.begin(user));
  }

  ngOnDestroy(): void {
    // One instance per scene row, so a leaked subscription here leaks per row, not per page.
    this.destroyed = true;
    this.userSub?.unsubscribe();
    this.viewSub?.unsubscribe();
    this.settleDecision(false);
    this.ui.closed(this);
  }

  /**
   * Eligibility is checked FIRST, before the item id and before any request, and on EVERY
   * emission: `logout()` never pushes to the user subject, so the previous account's record
   * survives in memory until the next sign-in fills it, and latching the first eligible emission
   * would hand one account's eligibility to the next.
   */
  private begin(user: any): void {
    if (!canUseStac(user)) {
      this.closeForm(false);
      this.viewSub?.unsubscribe();
      this.viewSub = undefined;
      this.covered = false;
      this.saved = null;
      this.unavailable = false;
      this.started = false;
      this.cdr.markForCheck();
      return;
    }
    if (this.started) return;
    this.started = true;
    // The task's STAC state, as the "Add to STAC" button reads it: no request of this row's own.
    this.viewSub = this.stac.view$(this.taskId).subscribe((view) => {
      const covered = !!view && view.sensors.get(this.sensorId) === 'on';
      if (!covered) this.closeForm(false);
      this.covered = covered;
      this.cdr.markForCheck();
    });
    this.itemId = StacTagsService.candidateItemId(this.taskId, this.sensorId, this.scene);
    if (!this.itemId) {
      // No id can be composed (no scene id yet, typically) — so no catalog item can exist for it.
      this.markUnavailable();
      return;
    }
    const itemId = this.itemId;
    Promise.all([this.stacTags.fieldDefinitions(), this.stacTags.annotationsForTask(this.taskId)])
      .then(([fields, map]) => {
        if (this.destroyed || !this.started) return;
        this.fields = fields;
        const stored = map.get(itemId);
        if (stored) {
          this.saved = copyAnnotation(stored);
          this.unavailable = false;
        } else {
          // The catalog answered and this scene is not in it.
          this.markUnavailable();
        }
      })
      .catch(() => {
        this.saved = null;
      })
      .then(() => this.cdr.markForCheck());
  }

  private markUnavailable(): void {
    this.closeForm(false);
    this.saved = {};
    this.unavailable = true;
    this.cdr.markForCheck();
  }

  // ─────────────────────────────────────────────────────────── the summary line

  /**
   * The set fields, in configured order — what the collapsed line shows. Rebuilt only when the
   * snapshot or the definitions are replaced (both are only ever replaced, never mutated): a fresh
   * array on every change-detection pass would make `*ngFor` recreate every chip each time, and a
   * hovered chip's tooltip with it.
   */
  get summary(): SummaryEntry[] {
    if (!this.summaryOf || this.summaryOf.saved !== this.saved || this.summaryOf.fields !== this.fields) {
      this.summaryOf = { saved: this.saved, fields: this.fields };
      this.summaryCache = buildSummary(this.saved || {}, this.fields, (field, value) => this.labelOf(field, value));
    }
    return this.summaryCache;
  }

  trackField(_index: number, field: AnnotationField): string {
    return field.member;
  }

  trackEntry(_index: number, entry: SummaryEntry): string {
    return entry.field.member;
  }

  labelOf(field: AnnotationField, value: string): string {
    return (field.labels && field.labels[value]) || value;
  }

  // ─────────────────────────────────────────────────────────── opening and closing

  toggle(): void {
    if (this.expanded) this.requestCollapse();
    else this.open();
  }

  /** Open the form on a copy of the snapshot — once whichever row is open has closed. */
  open(): void {
    if (this.expanded || this.unavailable || this.saved === null || !this.itemId) return;
    const itemId = this.itemId;
    const toggleBefore = this.toggleTop();
    this.ui.requestOpen(itemId, this).then((opened) => {
      if (!opened) return;
      if (this.destroyed || this.unavailable || this.saved === null) {
        // The row went away, or became unavailable, while another row was being asked.
        this.ui.closed(this);
        return;
      }
      // Open at once — the service now counts this row as the open one.
      this.draft = copyAnnotation(this.saved);
      this.hints = {};
      this.formError = '';
      this.confirming = null;
      this.expanded = true;
      this.cdr.detectChanges();
      this.keepToggleUnderPointer(toggleBefore);
      // The definitions may have been dropped by a save that showed them stale (R27): this
      // fetches them again, and is the cached copy otherwise. A field it adds joins the form,
      // "reload to edit" until this row has read it (`isKnown`).
      this.stacTags
        .fieldDefinitions()
        .then((fields) => {
          if (this.destroyed) return;
          this.fields = fields;
          this.cdr.markForCheck();
        })
        .catch(() => undefined);
    });
  }

  /** The disclosure control on an open row: a clean row closes, a dirty one asks first. */
  requestCollapse(): void {
    // While saving, nothing closes the row; while a question is showing, it is the answer that does.
    if (!this.expanded || this.saving || this.confirming) return;
    if (this.dirty) {
      this.confirming = 'collapse';
      this.cdr.markForCheck();
      return;
    }
    this.closeForm(true);
  }

  /** Drop the draft, show the snapshot. No request. */
  cancel(): void {
    if (this.saving) return;
    this.closeForm(true);
  }

  /** SceneMetadataUiService: another row wants to open. */
  requestClose(): Promise<boolean> {
    if (!this.expanded) return Promise.resolve(true);
    if (this.saving) return Promise.resolve(false);
    if (!this.dirty) {
      this.closeForm(false);
      return Promise.resolve(true);
    }
    this.confirming = 'switch';
    this.cdr.detectChanges();
    // The question may be in another table, off screen: bring it into view, and put focus on it.
    const keep = this.host.nativeElement.querySelector<HTMLElement>('.scene-meta-keep');
    if (keep) {
      if (typeof keep.scrollIntoView === 'function') keep.scrollIntoView({ block: 'nearest' });
      keep.focus();
    }
    return new Promise((resolve) => {
      this.settleDecision(false);
      this.decision = resolve;
    });
  }

  /** "Discard changes?" → Discard: as Cancel. */
  discard(): void {
    this.closeForm(this.confirming === 'collapse');
  }

  /** "Discard changes?" → Keep editing: the row stays open, the other one does not open. */
  keepEditing(): void {
    this.confirming = null;
    this.settleDecision(false);
    this.cdr.markForCheck();
  }

  /** Close the form. `restoreFocus`: this row's own control asked, so focus goes back to it. */
  private closeForm(restoreFocus: boolean): void {
    const wasOpen = this.expanded;
    this.expanded = false;
    this.draft = null;
    this.confirming = null;
    this.formError = '';
    this.hints = {};
    // Render the closed row NOW, before another row that is opening measures where its control
    // moved to (keepToggleUnderPointer) — and before focus goes back to this row's control.
    if (wasOpen && !this.destroyed) this.cdr.detectChanges();
    else this.cdr.markForCheck();
    this.settleDecision(true);
    if (wasOpen) this.ui.closed(this);
    if (wasOpen && restoreFocus && !this.destroyed) {
      const toggle = this.host.nativeElement.querySelector<HTMLElement>('.scene-meta-toggle');
      if (toggle) toggle.focus();
    }
  }

  private settleDecision(closed: boolean): void {
    const decide = this.decision;
    this.decision = null;
    if (decide) decide(closed);
  }

  /** Where the disclosure control is now, from the top of the viewport — or null. */
  private toggleTop(): number | null {
    const toggle = this.host.nativeElement.querySelector<HTMLElement>('.scene-meta-toggle');
    return toggle ? toggle.getBoundingClientRect().top : null;
  }

  /**
   * Opening this row may have closed one ABOVE it, moving this row up by that form's height —
   * scroll back by exactly the distance the control moved, so it stays under the pointer.
   */
  private keepToggleUnderPointer(before: number | null): void {
    const after = this.toggleTop();
    if (before === null || after === null) return;
    const moved = after - before;
    if (Math.abs(moved) < 1) return;
    const scroller = scrollParent(this.host.nativeElement);
    scroller.scrollTop += moved;
  }

  // ─────────────────────────────────────────────────────────── the draft

  /**
   * Has this row READ the field? A field the definitions gained after the task was loaded is not in
   * the snapshot, so its stored value is unknown here and it is not editable (see the header).
   */
  isKnown(field: AnnotationField): boolean {
    return this.saved !== null && Object.prototype.hasOwnProperty.call(this.saved, field.member);
  }

  tagsOf(field: AnnotationField): string[] {
    const value = this.draft ? this.draft[field.member] : null;
    return Array.isArray(value) ? value : [];
  }

  valueOf(field: AnnotationField): string {
    const value = this.draft ? this.draft[field.member] : null;
    return typeof value === 'string' ? value : '';
  }

  /**
   * A stored level this tab's definitions do not list — set by a newer tab after a migration added
   * it. Shown as its own option, so the dropdown says what is stored rather than "Select".
   */
  unlistedLevel(field: AnnotationField): string | null {
    const value = this.valueOf(field);
    return value !== '' && !(field.values || []).includes(value) ? value : null;
  }

  atLimit(field: AnnotationField): boolean {
    return typeof field.maxItems === 'number' && this.tagsOf(field).length >= field.maxItems;
  }

  inputId(field: AnnotationField): string {
    return `scene-meta-${this.itemId || 'x'}-${field.member}`;
  }

  /** A chip typed into a tagset: the draft only — no request. */
  addTag(field: AnnotationField, event: MatChipInputEvent): void {
    if (!this.draft || this.saving || !this.isKnown(field)) return;
    const tag = (event.value || '').trim();
    if (tag === '') {
      event.chipInput?.clear();
      return;
    }
    const problem = StacTagsService.tagProblem(field, tag, this.tagsOf(field));
    if (problem) {
      this.hints = { ...this.hints, [field.member]: problem };
      return;
    }
    event.chipInput?.clear();
    this.edit(field, this.tagsOf(field).concat(tag));
  }

  /** A chip removed: the draft only — no request. */
  removeTag(field: AnnotationField, tag: string): void {
    if (!this.draft || this.saving || !this.isKnown(field)) return;
    this.edit(field, this.tagsOf(field).filter((t) => t !== tag));
  }

  /** A text typed or a level picked: the draft only — no request. Empty is a clear. */
  setValue(field: AnnotationField, value: string): void {
    if (!this.draft || this.saving || !this.isKnown(field)) return;
    this.edit(field, value === '' ? null : value);
    const problem = StacTagsService.valueProblem(field, value);
    this.hints = { ...this.hints, [field.member]: problem || '' };
  }

  private edit(field: AnnotationField, value: string[] | string | null): void {
    this.draft = { ...this.draft, [field.member]: value };
    this.hints = { ...this.hints, [field.member]: '' };
    this.formError = '';
    if (this.confirming === 'collapse') this.confirming = null;
    this.cdr.markForCheck();
  }

  /**
   * ONLY the fields whose draft differs from the snapshot (R19) — a tag list compared as a set,
   * text after trimming — each as it would be sent: a list (`[]` to clear tags), trimmed text, a
   * level, or `null` to clear. A field this row has not read is never sent.
   */
  changes(): Annotation {
    const out: Annotation = {};
    if (!this.draft) return out;
    const saved = this.saved || {};
    for (const field of this.fields) {
      if (!this.isKnown(field)) continue;
      if (field.kind === 'tagset') {
        const next = tagList(this.draft[field.member]);
        if (!sameTags(next, tagList(saved[field.member]))) out[field.member] = next;
      } else {
        const next = scalar(this.draft[field.member]);
        if (next !== scalar(saved[field.member])) out[field.member] = next;
      }
    }
    return out;
  }

  get dirty(): boolean {
    return Object.keys(this.changes()).length > 0;
  }

  /**
   * A CHANGED value the definitions refuse — a too-long text, an unknown level. Unchanged values
   * are not judged: a value a newer tab stored under newer definitions must not lock this row.
   */
  get draftProblem(): boolean {
    const changed = this.changes();
    return this.fields.some(
      (f) => f.kind !== 'tagset' && Object.prototype.hasOwnProperty.call(changed, f.member) &&
        StacTagsService.valueProblem(f, this.valueOf(f)) !== null
    );
  }

  get canSave(): boolean {
    return this.expanded && !this.saving && this.dirty && !this.draftProblem;
  }

  // ─────────────────────────────────────────────────────────── saving

  save(): void {
    if (!this.canSave || !this.itemId) return;
    const changes = this.changes();
    this.saving = true;
    this.confirming = null;
    this.formError = '';
    this.cdr.markForCheck();
    this.stacTags
      .setAnnotation(this.taskId, this.itemId, changes)
      .then((stored) => {
        this.saving = false;
        // Gone, or no longer eligible, while the save was in flight: nothing to show it in.
        if (this.destroyed || !this.started) return;
        this.saved = copyAnnotation(stored);
        this.closeForm(true);
      })
      .catch((err: unknown) => {
        this.saving = false;
        if (this.destroyed || !this.started) return;
        const e = err instanceof StacTagsError ? err : null;
        if (e && e.kind === 'not_found') {
          // The item vanished between load and save (re-projected, withdrawn): the same disabled
          // row as a scene that was never in the catalog, rather than one that vanishes.
          this.markUnavailable();
        } else if (e && e.kind === 'invalid') {
          this.formError = e.message;
        } else {
          this.formError = (e ? e.message : 'The changes could not be saved.') + ' Your changes are kept — try Save again.';
        }
        this.cdr.markForCheck();
      });
  }
}

function buildSummary(
  saved: Annotation,
  fields: AnnotationField[],
  labelOf: (field: AnnotationField, value: string) => string
): SummaryEntry[] {
  const out: SummaryEntry[] = [];
  for (const field of fields) {
    const value = saved[field.member];
    if (field.kind === 'tagset') {
      if (Array.isArray(value) && value.length > 0) out.push({ field, tags: value });
    } else if (typeof value === 'string' && value !== '') {
      out.push({ field, text: field.kind === 'enum' ? labelOf(field, value) : value });
    }
  }
  return out;
}

/** The nearest ancestor that scrolls vertically, or the document's scrolling element. */
function scrollParent(el: HTMLElement): Element {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
  }
  return document.scrollingElement || document.documentElement;
}

function copyAnnotation(source: Annotation): Annotation {
  const out: Annotation = {};
  for (const member of Object.keys(source)) {
    const value = source[member];
    out[member] = Array.isArray(value) ? value.slice() : value;
  }
  return out;
}

function tagList(value: string[] | string | null | undefined): string[] {
  return Array.isArray(value) ? value.map((t) => t.trim()).filter((t) => t !== '') : [];
}

function sameTags(a: string[], b: string[]): boolean {
  const x = Array.from(new Set(a)).sort();
  const y = Array.from(new Set(b)).sort();
  return x.length === y.length && x.every((t, i) => t === y[i]);
}

function scalar(value: string[] | string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text === '' ? null : text;
}
